import { match } from 'ts-pattern'

import type { ResolutionContext } from '../../simulation.types'
import { parseTaskOutputReference } from '../parseTaskOutputReference'
import { readDataFromTask } from '../readDataFromTask'
import { isRecord } from './isRecord'
import type { ResolvedValue } from './resolveTaskData.types'

/**
 * Fills in one value of a task's `data`, whatever shape it is. Arrays and
 * objects are walked entry by entry, and anything else is a reference to look
 * up or a value to pass straight through.
 *
 * The first entry we can't fill in stops the walk and is what comes back, so a
 * half-filled object is never handed on.
 */
export const resolveValue = ({
  value,
  context
}: {
  value: unknown
  context: ResolutionContext
}): ResolvedValue => {
  if (Array.isArray(value)) return resolveArrayValue({ value, context })
  if (isRecord(value)) return resolveObjectValue({ value, context })

  return resolveReferenceValue({ value, context })
}

/** Keeps the order of the entries, since the executor reads them positionally. */
export const resolveArrayValue = ({
  value,
  context
}: {
  value: unknown[]
  context: ResolutionContext
}): ResolvedValue =>
  value.reduce<ResolvedValue>(
    (acc, entry) => {
      if ('unresolved' in acc) return acc

      const resolved = resolveValue({ value: entry, context })
      if ('unresolved' in resolved) return resolved

      return { value: [...(acc.value as unknown[]), resolved.value] }
    },
    { value: [] }
  )

export const resolveObjectValue = ({
  value,
  context
}: {
  value: Record<string, unknown>
  context: ResolutionContext
}): ResolvedValue =>
  Object.entries(value).reduce<ResolvedValue>(
    (acc, [key, entry]) => {
      if ('unresolved' in acc) return acc

      const resolved = resolveValue({ value: entry, context })
      if ('unresolved' in resolved) return resolved

      return {
        value: {
          ...(acc.value as Record<string, unknown>),
          [key]: resolved.value
        }
      }
    },
    { value: {} }
  )

/**
 * A single value that isn't a collection: either a reference to look up in the
 * context, or something written into the definition that we pass through.
 */
export const resolveReferenceValue = ({
  value,
  context
}: {
  value: unknown
  context: ResolutionContext
}): ResolvedValue =>
  match(parseTaskOutputReference(value))
    .with({ kind: 'literal' }, reference => ({ value: reference.value }))
    .with({ kind: 'workflowInput' }, reference =>
      read({
        source: context.workflowInput,
        path: reference.field,
        reference: value
      })
    )
    .with({ kind: 'task' }, reference =>
      read({
        source: context.taskOutputs[reference.taskReferenceName],
        path: reference.path,
        reference: value
      })
    )
    .exhaustive()

/**
 * Reads one reference. Nothing there means we couldn't work it out, so we report
 * the reference rather than passing `undefined` along as though it were a value
 * the workflow actually asked for.
 */
const read = ({
  source,
  path,
  reference
}: {
  source: unknown
  path: string
  reference: unknown
}): ResolvedValue => {
  const value = readDataFromTask({ source, path })

  return value === undefined ? { unresolved: String(reference) } : { value }
}
